import type { components } from "@/lib/generated/api-types";
export { MAX_UPLOAD_BYTES } from "@/lib/generated/api-constants";
import { auth } from "@/lib/firebaseClient";
import { startTimer } from "@/lib/perf";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3001";

const TOKEN_TTL_MS = 55 * 60_000; // 55 minutes — well within Firebase's 1-hour token lifetime
let cachedToken: string | null = null;
let cachedTokenExpiry = 0;

export function invalidateTokenCache() {
  cachedToken = null;
  cachedTokenExpiry = 0;
}

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue | undefined };
export type JsonRecord = { [key: string]: JsonValue | undefined };

export type ChatRole = "user" | "assistant";

// ---------------------------------------------------------------------------
// Generated types re-exported from the OpenAPI spec
//
// Where the OpenAPI spec uses `Any` (→ TS `unknown`), we narrow to `JsonValue`
// so consuming code does not need casts for normal JSON access patterns.
// ---------------------------------------------------------------------------

export type OrgRole = components["schemas"]["OrgRole"];
export type Organization = components["schemas"]["Organization"];
export type OrgMembershipRow = components["schemas"]["OrgMembershipRow"];
export type UserProfile = components["schemas"]["UserProfile"];
export type ChatRow = components["schemas"]["ChatRow"];
export type ChatMessageRow = Omit<components["schemas"]["ChatMessageRow"], "content" | "annotations"> & {
  content: JsonValue;
  annotations?: JsonValue;
};
export type ChatLiveSnapshot = Omit<components["schemas"]["ChatLiveSnapshot"], "events"> & {
  events: Array<{ id: string; event: JsonValue }>;
};
export type ChatDetail = Omit<components["schemas"]["GetChatResponse"], "chat" | "messages" | "live"> & {
  chat: ChatRow;
  messages: ChatMessageRow[];
  next_cursor?: string | null;
  has_more?: boolean;
  live?: ChatLiveSnapshot;
};

export type ChatCancelResult = { status: "idle" } | { status: "cancelled"; turn_id: string };

export type ProjectRow = Omit<components["schemas"]["ProjectRow"], "shared_with"> & {
  shared_with?: JsonValue;
};
export type ProjectDetail = Omit<components["schemas"]["ProjectDetail"], "shared_with"> & {
  shared_with?: JsonValue;
};
export type DocumentRow = components["schemas"]["DocumentRow"];
export type FolderRow = components["schemas"]["FolderRow"];
export type VersionRow = components["schemas"]["VersionRow"];
export type VersionsResponse = components["schemas"]["VersionsResponse"];
export type DocumentUrlResponse = components["schemas"]["DocumentUrlResponse"];
export type EditResolutionResponse = components["schemas"]["EditResolutionResponse"];
export type EditRow = components["schemas"]["EditRow"];
export type EditsListResponse = components["schemas"]["EditsListResponse"];
export type GetChatResponse = ChatDetail;
export type RegenerateResponse = components["schemas"]["RegenerateResponse"];
export type RegenerateTextResponse = components["schemas"]["RegenerateTextResponse"];
export type VadiumMessage = Omit<components["schemas"]["ChatMessageIn"], "files"> & {
  files?: Array<{ filename: string; document_id: string }>;
};

function apiUrl(path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${API_BASE_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

function withQuery(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  if (!query) {
    return path;
  }

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function getAuthHeaders() {
  const headers = new Headers();
  const currentUser = auth.currentUser;
  if (currentUser) {
    const now = Date.now();
    if (cachedToken && now < cachedTokenExpiry) {
      headers.set("Authorization", `Bearer ${cachedToken}`);
    } else {
      // Firebase deduplicates concurrent getIdToken() calls internally; double-writes are safe.
      const token = await currentUser.getIdToken();
      cachedToken = token;
      cachedTokenExpiry = now + TOKEN_TTL_MS;
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  return headers;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const headers = new Headers(init.headers);

  authHeaders.forEach((value, key) => headers.set(key, value));

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  let response = await fetch(apiUrl(path), {
    ...init,
    cache: "no-store",
    headers
  });

  // Retry once on 401 with a force-refreshed token — handles token expiry during
  // long batch operations where the cached token becomes stale.
  // Only retry for replayable bodies: FormData/ReadableStream are consumed on
  // first fetch and cannot be re-sent (the retry would send an empty body).
  const isReplayableBody =
    !(init.body instanceof FormData) &&
    !(init.body instanceof ReadableStream) &&
    !(init.body instanceof Blob) &&
    !(init.body instanceof URLSearchParams);
  if (response.status === 401 && auth.currentUser && isReplayableBody) {
    invalidateTokenCache();
    const freshToken = await auth.currentUser.getIdToken(/* forceRefresh */ true);
    // Don't write back to module-level cache — avoids races with concurrent
    // getAuthHeaders() calls during batch uploads. Let getAuthHeaders() repopulate
    // the cache naturally on the next call.
    headers.set("Authorization", `Bearer ${freshToken}`);
    response = await fetch(apiUrl(path), {
      ...init,
      cache: "no-store",
      headers
    });
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `API error: ${response.status}`);
  }

  if (response.status === 204 || response.headers.get("Content-Length") === "0") {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** Raw fetch with auth headers. No 401-retry — callers are one-shot actions
 * (display, download) unlikely to hit token expiry. Use apiRequest for
 * JSON endpoints that participate in batch workflows. */
export async function authorizedFetch(path: string, init: RequestInit = {}) {
  const authHeaders = await getAuthHeaders();
  const headers = new Headers(init.headers);
  authHeaders.forEach((value, key) => headers.set(key, value));

  return fetch(apiUrl(path), {
    ...init,
    cache: "no-store",
    headers
  });
}

export interface MultipartUploadOptions {
  file: File | Blob;
  filename?: string;
  fileField?: string;
  fields?: Record<string, string | Blob | null | undefined>;
  method?: "POST" | "PUT" | "PATCH";
}

export async function multipartUpload<T>(path: string, options: MultipartUploadOptions): Promise<T> {
  const formData = new FormData();
  const fileField = options.fileField ?? "file";

  if (options.filename) {
    formData.append(fileField, options.file, options.filename);
  } else {
    formData.append(fileField, options.file);
  }

  Object.entries(options.fields ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  return apiRequest<T>(path, {
    method: options.method ?? "POST",
    body: formData
  });
}

function jsonInit(method: string, body?: JsonValue): RequestInit {
  return {
    method,
    body: body === undefined ? undefined : JSON.stringify(body)
  };
}

export const api = {
  user: {
    ensureProfile: () => apiRequest<{ ok: true }>("/user/profile", { method: "POST" }),
    getProfile: () => apiRequest<UserProfile>("/user/profile"),
    updateProfile: (body: { displayName?: string | null; organisation?: string | null }) =>
      apiRequest<UserProfile>("/user/profile", jsonInit("PATCH", body as JsonRecord)),
    deleteAccount: () => apiRequest<void>("/user/account", { method: "DELETE" })
  },

  chat: {
    list: (limit?: number, archived?: boolean) => apiRequest<ChatRow[]>(withQuery("/chat", { limit, archived: archived ? "true" : undefined })),
    create: (projectId?: string | null, sourceDocumentId?: string | null) =>
      apiRequest<{ id: string }>("/chat/create", jsonInit("POST", { project_id: projectId ?? null, source_document_id: sourceDocumentId ?? null })),
    documentChats: (documentId: string) => apiRequest<ChatRow[]>(`/single-documents/${documentId}/chats`),
    get: (chatId: string, cursor?: string | null, limit?: number) => apiRequest<ChatDetail>(withQuery(`/chat/${chatId}`, { cursor, limit })),
    rename: (chatId: string, title: string) => apiRequest<{ id: string; title: string }>(`/chat/${chatId}`, jsonInit("PATCH", { title })),
    move: (chatId: string, projectId: string | null) =>
      apiRequest<{ id: string; project_id: string | null; shared_at: string | null; source_document_id: string | null }>(
        `/chat/${chatId}/move`,
        jsonInit("POST", { project_id: projectId })
      ),
    delete: (chatId: string) => apiRequest<void>(`/chat/${chatId}`, { method: "DELETE" }),
    archive: (chatId: string) => apiRequest<{ id: string; archived_at: string }>(`/chat/${chatId}/archive`, jsonInit("POST", {})),
    unarchive: (chatId: string) => apiRequest<{ id: string }>(`/chat/${chatId}/unarchive`, jsonInit("POST", {})),
    share: (chatId: string) => apiRequest<{ id: string; shared_at: string | null }>(`/chat/${chatId}/share`, jsonInit("POST", {})),
    unshare: (chatId: string) => apiRequest<{ id: string; shared_at: string | null }>(`/chat/${chatId}/unshare`, jsonInit("POST", {})),
    cancel: (chatId: string) => apiRequest<ChatCancelResult>(`/chat/${chatId}/cancel`, jsonInit("POST", {})),
    generateTitle: (chatId: string, message: string) => apiRequest<{ title: string }>(`/chat/${chatId}/generate-title`, jsonInit("POST", { message }))
  },

  orgs: {
    list: () => apiRequest<Organization[]>("/orgs"),
    create: (body: { name: string; slug: string }) => apiRequest<Organization>("/orgs", jsonInit("POST", body as JsonRecord)),
    get: (orgId: string) => apiRequest<Organization>(`/orgs/${orgId}`),
    update: (orgId: string, body: { name?: string; slug?: string; logo_url?: string | null }) =>
      apiRequest<Organization>(`/orgs/${orgId}`, jsonInit("PATCH", body as JsonRecord)),
    members: (orgId: string) => apiRequest<OrgMembershipRow[]>(`/orgs/${orgId}/members`),
    invite: (orgId: string, body: { email: string; role?: "member" | "admin" | "viewer" }) =>
      apiRequest<OrgMembershipRow>(`/orgs/${orgId}/invites`, jsonInit("POST", body as JsonRecord)),
    updateMember: (orgId: string, membershipId: string, body: { role?: OrgRole; status?: string }) =>
      apiRequest<OrgMembershipRow>(`/orgs/${orgId}/members/${membershipId}`, jsonInit("PATCH", body as JsonRecord)),
    removeMember: (orgId: string, membershipId: string) => apiRequest<void>(`/orgs/${orgId}/members/${membershipId}`, { method: "DELETE" })
  },

  projects: {
    list: (orgId?: string | null) => apiRequest<ProjectRow[]>(withQuery("/projects", { org_id: orgId })),
    create: (body: { name: string; cm_number?: string; shared_with?: string[]; org_id?: string | null }) =>
      apiRequest<ProjectDetail>("/projects", jsonInit("POST", body as JsonRecord)),
    get: (projectId: string) => apiRequest<ProjectDetail>(`/projects/${projectId}`),
    renameDocument: (projectId: string, documentId: string, filename: string) =>
      apiRequest<DocumentRow>(`/projects/${projectId}/documents/${documentId}`, jsonInit("PATCH", { filename })),
    uploadDocument: async (projectId: string, file: File) => {
      const timer = startTimer("doc.upload", { file_type: file.type, size_bytes: file.size });
      const result = await multipartUpload<DocumentRow>(`/projects/${projectId}/documents`, { file, filename: file.name });
      timer.stop();
      return result;
    },
    chats: (projectId: string) => apiRequest<ChatRow[]>(`/projects/${projectId}/chats`),
    createFolder: (projectId: string, body: { name: string; parent_folder_id?: string | null }) =>
      apiRequest<FolderRow>(`/projects/${projectId}/folders`, jsonInit("POST", body as JsonRecord)),
    updateFolder: (projectId: string, folderId: string, body: { name?: string; parent_folder_id?: string | null }) =>
      apiRequest<FolderRow>(`/projects/${projectId}/folders/${folderId}`, jsonInit("PATCH", body as JsonRecord)),
    deleteFolder: (projectId: string, folderId: string) => apiRequest<void>(`/projects/${projectId}/folders/${folderId}`, { method: "DELETE" }),
    moveDocumentToFolder: (projectId: string, documentId: string, folderId: string | null) =>
      apiRequest<DocumentRow>(`/projects/${projectId}/documents/${documentId}/folder`, jsonInit("PATCH", { folder_id: folderId }))
  },

  documents: {
    listStandalone: () => apiRequest<DocumentRow[]>("/single-documents"),
    uploadStandalone: async (file: File) => {
      const timer = startTimer("doc.upload", { file_type: file.type, size_bytes: file.size });
      const result = await multipartUpload<DocumentRow>("/single-documents", { file, filename: file.name });
      timer.stop();
      return result;
    },
    deleteStandalone: (documentId: string) => apiRequest<void>(`/single-documents/${documentId}`, { method: "DELETE" }),
    displayResponse: (documentId: string, versionId?: string) => authorizedFetch(withQuery(`/single-documents/${documentId}/display`, { version_id: versionId })),
    downloadZipResponse: (documentIds: string[]) =>
      authorizedFetch("/single-documents/download-zip", {
        ...jsonInit("POST", { document_ids: documentIds }),
        headers: { "Content-Type": "application/json" }
      }),
    url: (documentId: string, versionId?: string) => apiRequest<DocumentUrlResponse>(withQuery(`/single-documents/${documentId}/url`, { version_id: versionId })),
    docxResponse: (documentId: string, versionId?: string) => authorizedFetch(withQuery(`/single-documents/${documentId}/docx`, { version_id: versionId })),
    trackedChangeIds: (documentId: string, versionId?: string) =>
      apiRequest<{ ids: Array<{ kind: "ins" | "del"; w_id: string }> }>(withQuery(`/single-documents/${documentId}/tracked-change-ids`, { version_id: versionId })),
    versions: (documentId: string) => apiRequest<VersionsResponse>(`/single-documents/${documentId}/versions`),
    saveEditorVersion: (documentId: string, blob: Blob, filename: string) =>
      multipartUpload<VersionRow>(`/single-documents/${documentId}/versions`, { file: blob, filename, fields: { source: "editor_save" } }),
    renameVersion: (documentId: string, versionId: string, displayName: string | null) =>
      apiRequest<VersionRow>(`/single-documents/${documentId}/versions/${versionId}`, jsonInit("PATCH", { display_name: displayName })),
    listEdits: (documentId: string, status?: string) => apiRequest<EditsListResponse>(withQuery(`/single-documents/${documentId}/edits`, { status })),
    acceptEdit: (documentId: string, editId: string) => apiRequest<EditResolutionResponse>(`/single-documents/${documentId}/edits/${editId}/accept`, { method: "POST" }),
    rejectEdit: (documentId: string, editId: string) => apiRequest<EditResolutionResponse>(`/single-documents/${documentId}/edits/${editId}/reject`, { method: "POST" }),
    regenerate: (documentId: string, body: { selected_text: string; instruction?: string; case_context?: string; source_spans?: Array<Record<string, unknown>> | null }) =>
      apiRequest<RegenerateResponse>(`/single-documents/${documentId}/regenerate`, jsonInit("POST", body as JsonRecord)),
    regenerateText: (documentId: string, body: { selected_text: string; surrounding_text?: string; instruction?: string }) =>
      apiRequest<RegenerateTextResponse>(`/single-documents/${documentId}/regenerate-text`, jsonInit("POST", body as JsonRecord))
  },

};
